/**
 * Utility class which presents interfaces for selection, creation, and destruction of `MapServiceBase` instances and underlying `MapService` models.
 * @module app/views/map/ServiceSelector
 */
import _ from "underscore";
import Field from "core/models/Field";
import FormInputView from "core/views/FormInputView";
import MapService from "app/models/MapService";
import MapServiceCollection from "app/models/MapServiceCollection";
import $ from "jquery";
import ServiceFactory from "app/models/map/ServiceFactory";
import ModalView from "core/views/ModalView";
import ArgHelper from "app/views/map/helpers/ArgHelper";
import auth from "app/auth";
import session from "app/Session";
import modals from "core/views/modals";
import isMobile from "core/is-mobile";

var defaultOptions = {
  newLabel: "New service name", //label for new service name
  newName: "New Service", //name for creating new service
  fieldLabel: "Select a Service", //label for service selection dropdown
  confirmText: "Next", //title of "accept" button
  type: "primary",
  modalClass: "ServiceSelector",
  title: "Service Select", //title of confirmation dialog,
  create: function () {}, //function to be called if a service is created. first argument is the object created
  filter: function (service) {
    return true;
  }, //filter function to be applied to individual service models (MapServices) before presenting selection
  canCreate: false, //true to indicate a service can be created
};

var ServiceSelector = {
  /**
   * Presents an interface for selecting a `MapServiceBase` instance.
   * @param  {Array}   services      An array of `MapServiceBase` models representing the currently-selected services.
   * @param  {Array}   serviceModels An array of `MapService` models representing the possible selections.
   * @param {object} [options] The options hash.
   * @param {string} options.newLabel:'New service name' Label text for the input used to enter the name of a new service.
   * @param {string} options.newName:'New Service' Text for selection option used to indicate creation of a new service is desired.
   * @param {string} options.fieldLabel:'Select a Service' Label for selection control.
   * @param {string|object} options.confirmText:'Next' Label text or options for control used to confirm selection.
   * @param {string} options.title:'Service Select' Label text for entire UI block.
   * @param {Boolean} options.canCreate True to indicate a service can be created.
   * @param {Function} [options.filter] Filter function which, given a `MapService`, shall return `true` if the service is desired in the list of selectable items, otherwise `false`.
   * @param {Function} [options.create] Function which shall be called if a service is created *successfully*. The first argument is the `MapServiceBase` instance that was created.
   * @param  {Function} cb            A callback which shall expect two arguments. The first is an error, if any, and the second is the selected `MapServiceBase`, if no error occurred.
   */
  selectService: async function (services, serviceModels, cb) {
    var firstService = _.first(serviceModels);

    var options = ArgHelper.parseOptions(arguments, 2, defaultOptions);
    var serviceModelList = _.map(
      _.filter(serviceModels, options.filter),
      function (service) {
        return {
          value: service.id,
          text: service.get("name"),
        };
      }
    );

    var canCreate = options.canCreate;
    var serviceSelectModel = {
      service: canCreate || !firstService ? "" : firstService.id,
    };

    var serviceModelLinks = {};
    /*service model id -> service*/

    _.each(services, function (service) {
      serviceModelLinks[service.service.id] = service;
    });

    cb = ArgHelper.parseCallback(arguments, 2);

    if (canCreate) {
      serviceModelList.unshift({
        value: "",
        text: options.newName,
      });
    }
    //now we have a list of values to be used for a dropdown
    var serviceField = new Field({
      name: "service",
      label: options.fieldLabel,
      values: serviceModelList,
    });
    var serviceSelect = new FormInputView({
      model: serviceSelectModel,
      field: serviceField,
    });

    //create a service model to use if the user wants to create a service
    var mapService = new MapService({
      external: false,
      defaultVisibility: true,
    });

    mapService.collection = new MapServiceCollection([]); //basically just need this to get the URL
    var nameField = new Field(MapService.fields["name"]).decorate({
      label: options.newLabel,
    });
    var nameEntry = new FormInputView({
      model: mapService,
      field: nameField,
    });

    serviceSelect.on("change", function (value) {
      if (value === "" && canCreate) {
        //nameEntry.$el.show();
        nameEntry.$el.css("visibility", "");
      } else {
        //nameEntry.$el.hide();
        nameEntry.$el.css("visibility", "hidden");
      }
    });

    serviceSelect.render();
    nameEntry.render();

    if (!canCreate) {
      nameEntry.$el.hide();
    }

    var deferred = new $.Deferred();

    function acceptExistingService(serviceId) {
      var service = serviceModelLinks[serviceId];

      if (!service) {
        service = _.detect(serviceModels, function (service) {
          return service.id == serviceId;
        });
        ServiceSelector.createService(service, function (err, service) {
          service
            .load()
            .done(function () {
              if (err) {
                return deferred.reject(err);
              }

              deferred.resolve(service);
            })
            .fail(function (err) {
              deferred.reject(err);
            });
        });
      } else {
        deferred.resolve(service);
      }
    }

    var confirmOptions = {
      className: isMobile ? "btn-lg" : "",
    };

    if (_.isObject(options.confirmText)) {
      confirmOptions = _.extend(confirmOptions, options.confirmText);
    } else {
      confirmOptions.text = options.confirmText;
    }

    var content = serviceSelect.$el.add(nameEntry.$el);
    var modalView = modals.confirm(
      options.title,
      content,
      confirmOptions,
      function () {
        if (serviceSelect.model.get("service") === "") {
          //if creating a new serviceg
          mapService.save(null, {
            success: async function () {
              try {
                await session.refreshCurrentUser();
                ServiceSelector.createService(
                  mapService,
                  function (err, service) {
                    service
                      .load()
                      .done(function () {
                        if (err) {
                          return cb(err);
                        }

                        options.create(service);
                        deferred.resolve(service);
                      })
                      .fail(function (err) {
                        deferred.reject(err);
                      });
                  }
                );
              } catch (e) {
                deferred.reject(e);
              }
            },
            error: function (model, response) {
              deferred.reject(response);
            },
          });
        } else {
          acceptExistingService(serviceSelect.model.get("service"));
        }
      },
      {
        text: "Cancel",
        className: isMobile ? "btn-lg" : "",
      },
      function () {
        deferred.reject(true);
      },
      options.modalClass
    );

    modalView.once(
      "hidden remove",
      _.once(function () {
        deferred
          .done(function (result) {
            cb(null, result);
          })
          .fail(function (err) {
            cb(err);
          });
      })
    );

    var createButton = modalView.buttons[1];

    serviceSelect.on("change", function (value) {
      if (value) {
        //enable create button
        createButton.enable();
        //modalView.hide();
        //acceptExistingService(serviceSelectModel.service);
      } else {
        if (mapService.get("name")) {
          //enable create button
          createButton.enable();
        } else {
          //disable create button
          createButton.disable();
        }
      }
    });
    nameEntry.$("input").on("change keyup", function () {
      var self = nameEntry.$("input");

      if (self.val()) {
        createButton.enable();
      } else {
        createButton.disable();
      }
    });

    if (serviceSelectModel.service) {
      createButton.enable();
    } else {
      createButton.disable();
    }
  },

  /**
   * Presents an interface for selecting a `MapService` model.
   * @param  {Array}   services      An array of `MapServiceBase` models representing the currently-selected services.
   * @param  {Array}   serviceModels An array of `MapService` models representing the possible selections.
   * @param {object} [options] The options hash.
   * @param {string} options.newLabel:'New service name' Label text for the input used to enter the name of a new service.
   * @param {string} options.newName:'New Service' Text for selection option used to indicate creation of a new service is desired.
   * @param {string} options.fieldLabel:'Select a Service' Label for selection control.
   * @param {string} options.confirmText:'Next' Label text for control used to confirm selection.
   * @param {string} options.title:'Service Select' Label text for entire UI block.
   * @param {Boolean} options.canCreate True to indicate a service can be created.
   * @param {Function} [options.filter] Filter function which, given a `MapService`, shall return `true` if the service is desired in the list of selectable items, otherwise `false`.
   * @param {Function} [options.create] Function which shall be called if a service is created *successfully*. The first argument is the `MapService` model that was created.
   * @param  {Function} cb            A callback which shall expect two arguments. The first is an error, if any, and the second is the selected `MapService`, if no error occurred.
   */
  selectServiceModel: function (services, serviceModels, cb) {
    var options = ArgHelper.parseOptions(arguments, 2, defaultOptions);

    cb = ArgHelper.parseCallback(arguments, 2);
    var create = options.create;
    var deferred = new $.Deferred();

    options.create = function () {
      deferred.done(function (service) {
        create(service.service);
      });
    };

    ServiceSelector.selectService(
      services,
      serviceModels,
      options,
      function (err, service) {
        if (err) {
          return cb(err);
        }

        deferred.resolve(service);
        cb(null, service.service);
      }
    );
  },

  /**
   * Generates a `MapServiceBase` instance from a given `MapService` model.
   * @param  {MapService}   serviceModel The model from which to generate the instance.
   * @param  {Function} [cb]           A function which shall expect two arguments. The first is an error, if any, and the second is the `MapServiceBase` which was generated, if the operation was successful.
   * @return {MapServiceBase}                If `cb` is not specified, the `MapServiceBase` that was created.
   */
  createService: function (serviceModel, cb) {
    return ServiceFactory.createService(serviceModel, cb);
  },

  /**Prompts a user regarding the deletion of a service.
	 @param {MapService} serviceModel The service model to delete.
	 @param {Function} cb The callback to be called on success or error. If the first argument is `true`,
	 the operation was cancelled. If it is just "truthy", it contains an error message. Otherwise,
	 the operation succeeded.*/
  destroyService: function (serviceModel, cb) {
    var modal;
    var typeName = "Service";
    var CANCEL = "Cancel";
    var DELETE = "Delete";
    var DELETE_TITLE = DELETE + " " + typeName;
    var DELETE_PROMPT =
      "Would you like to delete this service?<br/>Deleting will <strong>permanently destroy</strong> layer, geometry, and file data.";
    var REALLY_DELETE_PROMPT =
      "Are you sure you want to <strong>permanently delete</strong> this service? This action cannot be undone.";
    var REALLY_DELETE = "Permanently " + DELETE;
    var REALLY_REALLY_DELETE_PROMPT =
      'Enter your password to confirm your wish to <strong>permanently delete</strong> this service and <i>all data</i> by typing "permanently delete" below.<br/><input class="form-control" type="text" name="delete-confirm"/>';
    var REALLY_REALLY_DELETE = "Confirm " + DELETE;

    var CALM_DELETE_PROMPT =
      "Are you sure you want to delete this map service?";

    if (serviceModel.get("external")) {
      modals.confirm(
        DELETE_TITLE,
        CALM_DELETE_PROMPT,
        {
          type: "danger",
          text: DELETE,
        },
        function () {
          serviceModel.destroy({
            success: function () {
              cb();
            },
            error: function (model, response) {
              cb(response);
            },
          });
          cb();
        },
        CANCEL,
        function () {
          cb(true); //pass "true" to cancel
        }
      );
    } else {
      modal = new ModalView({
        title: DELETE_TITLE,
        modalClass: "initial-delete",
        buttons: [
          {
            text: CANCEL,
            click: function () {
              modal.remove();
              cb(true);
            },
          },
          {
            text: DELETE,
            click: function () {
              modal.remove();
              modal = new ModalView({
                title: DELETE_TITLE,
                modalClass: "serious-delete",
                buttons: [
                  {
                    text: CANCEL,
                    click: function () {
                      modal.remove();
                      cb(true);
                    },
                  },
                  {
                    text: REALLY_DELETE,
                    click: function () {
                      modal.remove();
                      modal = new ModalView({
                        title: REALLY_REALLY_DELETE,
                        modalClass: "confirm-delete",
                        buttons: [
                          {
                            text: CANCEL,
                            click: function () {
                              modal.remove();
                              cb(true);
                            },
                          },
                          {
                            text: REALLY_REALLY_DELETE,
                            click: function () {
                              modal.remove();

                              console.log("clicked delete");
                              serviceModel.destroy({
                                headers: {
                                  Authorization: "Bearer " + auth.token,
                                },
                                success: function () {
                                  alert("Service deleted successfully.");
                                  cb();
                                },
                                error: function (model, response) {
                                  alert("Unable to delete service.");
                                  console.log(response);
                                  cb(response);
                                },
                              });
                            },
                            type: "danger",
                          },
                        ],
                      });
                      modal.render();
                      modal.setContent(REALLY_REALLY_DELETE_PROMPT);
                      modal.show();
                    },
                    type: "danger",
                  },
                ],
              });
              modal.render();
              modal.setContent(REALLY_DELETE_PROMPT);
              modal.show();
            },
            type: "danger",
          },
        ],
      });
      modal.render();
      modal.setContent(DELETE_PROMPT);
      modal.show();
    }
  },
};

export default ServiceSelector;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/ServiceSelector.js
// module id = 128
// module chunks = 1